import {
  ActionName,
  ConditionOperator,
  PropertyConditionEvaluationAttributes,
  PropertyConditionName,
  PropertyConditionOperatorMap,
  PropertyConditionType,
  WorkflowConfigurationMap,
  WorkflowScopeType,
} from '@invenco/common-domain/enums';

import { assertNever } from '@/shared/helpers';

import { type ActionDefinition, actionDefinitions } from './action-definitions';
import { type FieldDescriptor, multiSelect, none, tags, text } from './field-descriptor';
import { operatorLabels } from './operator-labels';
import { type PropertyDefinition, propertyDefinitions } from './property-definitions';

/**
 * The one place that answers "what does property X render as for operator Y in
 * scope Z". It merges the backend semantic maps (`@invenco/common-domain`) with
 * this kit's presentation layer.
 */

/** Operators with no operand — the value input collapses to `none`. */
const NO_VALUE_OPERATORS: ReadonlySet<ConditionOperator> = new Set([
  ConditionOperator.IS_EMPTY,
  ConditionOperator.IS_NOT_EMPTY,
]);

/** Operators that match against a set — the value input becomes multi-valued. */
const MULTI_VALUE_OPERATORS: ReadonlySet<ConditionOperator> = new Set([
  ConditionOperator.IN,
  ConditionOperator.NOT_IN,
  ConditionOperator.IN_CASE_INSENSITIVE,
  ConditionOperator.NOT_IN_CASE_INSENSITIVE,
  ConditionOperator.ALL_IN_GROUP,
  ConditionOperator.ALL_NOT_IN_GROUP,
  ConditionOperator.NONE_IN_GROUP,
  ConditionOperator.NONE_NOT_IN_GROUP,
  ConditionOperator.SOME_IN_GROUP,
  ConditionOperator.SOME_NOT_IN_GROUP,
]);

/** The operators a property accepts, from the backend operator map. */
export function getPropertyOperators(property: PropertyConditionName): ConditionOperator[] {
  const { type, inputType } = PropertyConditionEvaluationAttributes[property];
  return PropertyConditionOperatorMap[type]?.[inputType ?? type] ?? [];
}

export function getOperatorLabel(operator: ConditionOperator): string {
  return operatorLabels[operator];
}

/** The multi-valued form of a scalar value input. */
function toMultiValue(input: FieldDescriptor): FieldDescriptor {
  switch (input.kind) {
    case 'select':
      return multiSelect({ options: input.options });
    case 'entity-select':
      return { ...input, multi: true };
    case 'multi-select':
    case 'tags':
    case 'none':
      return input;
    // Free text/number/datetime have no fixed option list, so multiple values
    // are collected as free-form tags.
    case 'text':
    case 'number':
    case 'datetime':
      return tags();
    default:
      return assertNever(input);
  }
}

/** The value input for a property under a specific operator. */
export function resolveValueInput(
  property: PropertyConditionName,
  operator: ConditionOperator,
): FieldDescriptor {
  if (NO_VALUE_OPERATORS.has(operator)) {
    return none();
  }

  const { input } = propertyDefinitions[property];
  return MULTI_VALUE_OPERATORS.has(operator) ? toMultiValue(input) : input;
}

/**
 * The input for a property's sub-property ("Field"), or `null` if it has none.
 *
 * Key-value properties (today only `ORDER_CUSTOM_ATTRIBUTE`) match a named field
 * within a map: the backend rejects them with "subProperty is required" unless a
 * `subProperty` accompanies the value, so the editor must render it as a required
 * field alongside the value input. Derived from the backend type, so new
 * key-value properties are covered without touching this kit.
 */
export function resolveSubPropertyInput(property: PropertyConditionName): FieldDescriptor | null {
  const { type } = PropertyConditionEvaluationAttributes[property];
  return type === PropertyConditionType.keyValue ? text() : null;
}

/** The full presentation entry for a property. */
export function getPropertyDefinition(property: PropertyConditionName): PropertyDefinition {
  return propertyDefinitions[property];
}

export function getPropertyLabel(property: PropertyConditionName): string {
  return propertyDefinitions[property].label;
}

/** The properties available in a scope, in backend-declared order. */
export function getScopeProperties(scope: WorkflowScopeType): PropertyConditionName[] {
  return Object.keys(
    WorkflowConfigurationMap[scope].propertyConditionEvaluationAttributes,
  ) as PropertyConditionName[];
}

/**
 * Unknown action types (deploy skew: stored data newer than this build)
 * degrade to a disabled paramless fallback — renderable, nothing to validate,
 * excluded from the add-action select — instead of crashing every consumer.
 */
export function getActionDefinition(action: ActionName): ActionDefinition {
  return (
    (actionDefinitions[action] as ActionDefinition | undefined) ?? {
      title: action,
      params: [],
      disabled: true,
    }
  );
}

/** True when the registry declares the action; false means the paramless fallback. */
export function hasActionDefinition(action: ActionName): boolean {
  return action in actionDefinitions;
}

/** Whether an action is backend-recognised but not yet supported in the UI. */
export function isActionDisabled(action: ActionName): boolean {
  return getActionDefinition(action).disabled ?? false;
}

/** The actions available in a scope, in backend-declared order. */
export function getScopeActions(scope: WorkflowScopeType): ActionName[] {
  return Object.keys(WorkflowConfigurationMap[scope].actionTypeOutcomeAttributes) as ActionName[];
}
